import * as React from "react";
import { ComboBox, Dropdown, IComboBox, IComboBoxOption, IDropdownOption, ResponsiveMode } from "@fluentui/react";
import { ChoiceProps } from "../../interfaces/props/ChoiceProps";
import { logger } from "../../../../utilities/logger";
import { SharePointMetadataBooleans } from "../../config/sharepointConstants";

const getValueAsArray = (value: any): any[] => {
  return Array.isArray(value) ? value : value ? [value] : [];
}

const getOptions = (allowFreeForm: boolean, defaultOptions: any[], inputValue: any) => {
  const options = defaultOptions?.map(item => ({ key: item, text: item })) || [];
  if (allowFreeForm && Array.isArray(options) && inputValue) {
    const initialSelection: any[] = getValueAsArray(inputValue);
    for (let i = 0; i < initialSelection.length; i++) {
      const selectedValue = initialSelection[i] as string;
      if (!options.some(option => option.key && ((option.key as string).trim().toLowerCase() === selectedValue.trim().toLowerCase()))) {
        options.push({ key: selectedValue, text: selectedValue });
      }
    }
  }
  return options;
}

export const CheckboxMultiColumn: React.FunctionComponent<ChoiceProps> = ({
  displayName,
  isRequired,
  Choices,
  inputValidator,
  updateInputValidationStatus,
  error,
  inputValue
}: ChoiceProps) => {
  const [allowFreeForm] = React.useState<boolean>(inputValue?.xml?.FillInChoice === SharePointMetadataBooleans.true);
  const [selectedKeys, setSelectedKeys] = React.useState<string[]>();
  const [options, setOptions] = React.useState<any[]>(getOptions(allowFreeForm, Choices, inputValue?.value));

  const isFirstRun = React.useRef(true);

  React.useEffect(() => {
    if (inputValue?.value) {
      setSelectedKeys(getValueAsArray(inputValue?.value));
    }
  }, [inputValue?.value]);

  React.useEffect(() => {
    try {
      if (isFirstRun.current) {
        isFirstRun.current = false;
        return;
      }
      updateInputValidationStatus(
        inputValidator.validate(inputValue?.value
          ? getValueAsArray(inputValue?.value)
          : null)
      );
    } catch (e) {
      logger(e, "checboxMultiColumn (useEffect 2)")
    }
  }, [inputValue?.updatedFromDataSourceToggle]);

  const onChange = React.useCallback(
    (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
      if (item) {
        let selected = item.selected
          ? [...(selectedKeys || []), item.key as string]
          : selectedKeys?.filter(key => key !== item.key);
        setSelectedKeys(selected);
        updateInputValidationStatus(inputValidator.validate(selected));
      }
    },
    [selectedKeys, inputValidator]
  );

  const onComboChange = React.useCallback(
    (event: React.FormEvent<IComboBox>, option?: IComboBoxOption, index?: number, value?: string): void => {
      let selected = option?.selected;
      if (!option && !value?.trim()) return;

      if (!option && value) {
        selected = true;
        option = { key: value, text: value };
        setOptions(prevOptions => [...prevOptions, option]);
      }

      if (option) {
        const selection = selected ? [...(selectedKeys || []), option!.key as string] : selectedKeys?.filter(k => k !== option!.key);
        setSelectedKeys(selection);
        updateInputValidationStatus(inputValidator.validate(selection));
      }
    },
    [selectedKeys, inputValidator]
  );

  const onBlur = (event): void => {
    updateInputValidationStatus(inputValidator.validate(selectedKeys));
  };

  return allowFreeForm
    ?
    <ComboBox
      multiSelect
      allowFreeform
      useComboBoxAsMenuWidth
      disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
      selectedKey={selectedKeys}
      label={displayName}
      options={options}
      onChange={onComboChange}
      required={isRequired()}
      errorMessage={error}
    />
    :
    <Dropdown
      // responsiveMode={ResponsiveMode.small}
      disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
      placeholder="Select"
      label={displayName}
      selectedKeys={selectedKeys}
      onChange={onChange}
      onBlur={onBlur}
      multiSelect
      required={isRequired()}
      options={options}
      errorMessage={error}
      className="xb-sp-input"
    />;
};

export default CheckboxMultiColumn;
